import React, { useState, useEffect, useRef } from 'react';
import { LeftArrowIcon } from 'assets/icons/AntDeisgn_icons/iconStyle';
import { BackButton } from 'component/button_components/button_general/ButtonGeneralStyle';
import { AdminPageSizeWrap, AdminPageWrap, AdminTitleWrap } from 'pages/admin_page/style';
import { Checkbox, Form, Input } from 'antd';
import ReactDOM from 'react-dom';
import { AntDivider } from 'component/form_component/style';
import {
  EmailFigCaptionWrap,
  EmailWrap,
  FigCaption,
  FormContentWrap,
  FormItem,
  LicenseInputWrap,
  LicenseKeyWrap,
  StatusLabel,
  StatusValue,
  StatusWrap,
} from './style';
import { useDispatch, useSelector } from 'react-redux';
import { activateLicense, deActivateLicense, getLicenseDetails } from 'redux/slices/licenseSlice';
import { SweetSweetAlert } from 'component/sweetalert_components';
import { LoaderComponent } from 'component/loader_component/style';
import Loader from 'component/loader_component';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'antd/es/form/Form';
import Swal from 'sweetalert2';
import CustomToggleButton from 'component/button_components/custom_toggle';
import { ContentWrapper } from 'pages/settings_page/style';
import { AllowedDomainListWrap } from 'pages/brandings/style';
import { getLicenseTier } from 'redux/slices/setupSlice';

export default function License() {
  const inputRefs = useRef([]);
  const [form] = useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { licenseDetails } = useSelector((state) => state.licenseSlice);
  const [active, setActive] = useState(licenseDetails?.isActived);
  const [loading, setLoading] = useState(false);
  const [reCall, setReCall] = useState(false);
  const [licenseStatusValue, setLicenseStatusValue] = useState('No License');
  const { domainFromApi } = useSelector((state) => state.domainData);
  const options = domainFromApi.map((domain) => ({
    label: `${domain.domainName}(${domain.fqdn})`,
    value: domain.id,
    disabled: domain.isPrimaryDomain,
  }));
  const [licenseParts, setLicenseParts] = useState(['', '', '', '']);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(!licenseParts.every((part) => part.length === 4));
  const tierInfoRef = useRef(null);
  const selectedDomainRef = useRef([]);

  useEffect(() => {
    dispatch(getLicenseDetails());
  }, [reCall]);

  useEffect(() => {
    if (licenseDetails) {
      form.setFieldsValue({ email: licenseDetails.email });
      setLicenseParts(licenseDetails.licenseKey.split('-').map((part) => part.substring(0, 4)));
      if (licenseDetails.isActived) {
        setLicenseStatusValue('Activated');
      } else if (licenseDetails.isActived === false) {
        setLicenseStatusValue('Deactivated');
      }
    }
  }, [licenseDetails]);

  useEffect(() => {
    setIsSubmitDisabled(!licenseParts.every((part) => part.length === 4));
  }, [licenseParts]);

  const handleInputChange = (index, value) => {
    value = value.toUpperCase();
    if (value.length <= 4) {
      const newParts = [...licenseParts];
      newParts[index] = value;
      setLicenseParts(newParts);
      if (value.length === 4 && index < 3) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    const _licenseParts = [...licenseParts];
    const pasteData = e.clipboardData.getData('text/plain').trim().toUpperCase();
    if (pasteData) {
      const breakPoints = /\-|\s|\$|\_/;
      const splitValue = pasteData.split(breakPoints, 4).join('');
      const modifiedValue = splitValue.match(/(.{1,4})/g);

      modifiedValue.forEach((ele) => {
        if (index < _licenseParts.length) {
          _licenseParts[index] = ele;
          if (_licenseParts[index].length === 4 && index < 3) {
            inputRefs.current[index + 1].focus();
          } else if (index < 3) {
            inputRefs.current[index].focus();
          }
          index += 1;
        }
      });

      setLicenseParts(_licenseParts);
    }
  };

  const { Group: CheckboxGroup } = Checkbox;
  // Function to check condition before activating
  const checkCondition = async (value) => {
    setLoading(true);
    try {
      const response = await dispatch(getLicenseTier(value)); // Fetch the tierInfo
      const fetchedTierInfo = parseInt(response.payload?.data?.tierInfo);
      if (!fetchedTierInfo) {
        setLoading(false);
        return true;
      }
      tierInfoRef.current = fetchedTierInfo; // Store the fetched value in the ref
      if (domainFromApi.length > fetchedTierInfo) {
        const selectedDomains = await promptUserToSelectDomains(fetchedTierInfo);
        if (!selectedDomains) {
          setLoading(false);
          return false;
        }
      }
      return true;
    } catch (error) {
      setLoading(false);
      return false;
    }
  };

  // Function to prompt user to select domains
  const promptUserToSelectDomains = async (tierInfo) => {
    return Swal.fire({
      title: `You currently have ${domainFromApi.length} out of ${tierInfo} allowed AD domains. Please select atleast ${
        domainFromApi.length - tierInfo
      } to remove.`,
      html: `<div id="checkbox-group-container"></div>`,
      icon: 'info',
      showConfirmButton: true,
      confirmButtonText: 'Remove and Activate License',
      showCancelButton: true,
      confirmButtonColor: 'var(--bg-button-default-color)',
      cancelButtonColor: 'green',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      width: '500px',
      customClass: {
        container: 'custom-swal-container',
        icon: 'custom-swal-icon',
        title: 'custom-swal-title',
      },
      preConfirm: () => {
        const selectedCheckboxes = Array.from(document.querySelectorAll('#checkbox-group-container input:checked')).map(
          (checkbox) => checkbox.value
        );

        if (selectedCheckboxes.length < domainFromApi.length - tierInfo) {
          Swal.showValidationMessage(`Please select atleast ${domainFromApi.length - tierInfo} domains to remove.`);
          return false; // Prevent closing the modal
        }
        return selectedCheckboxes;
      },
      didOpen: () => {
        // Render the Ant Design Checkbox.Group when the modal is opened
        ReactDOM.render(
          <>
            <h3 style={{ textAlign: 'left', margin: '0' }}>Select Domain(s):</h3>
            <AllowedDomainListWrap>
              <CheckboxGroup options={options} />
            </AllowedDomainListWrap>
          </>,
          document.getElementById('checkbox-group-container')
        );
      },
      willClose: () => {
        // Cleanup the React component when the modal closes
        ReactDOM.unmountComponentAtNode(document.getElementById('checkbox-group-container'));
        setLoading(false);
      },
    }).then((result) => {
      if (result.isConfirmed) {
        selectedDomainRef.current = result.value;
        return true;
      }
    });
  };

  const handleSubmit = async (value) => {
    value.licenseKey = licenseParts.join('-');
    value.productCode = 'adweb';
    value.uid = '';
    if (!licenseDetails?.isActived) {
      const conditionResult = await checkCondition(value);
      if (!conditionResult) {
        return;
      }
    }
    value.tierInfo = licenseDetails?.isActived ? licenseDetails.tierInfo : tierInfoRef.current;
    value.domains = selectedDomainRef.current;
    if (active) {
      try {
        setLoading(true);
        const response = await dispatch(activateLicense(value));
        if (response.payload && response.payload.status === 200) {
          const result = await Swal.fire({
            icon: 'success',
            title: response.payload.data,
            showConfirmButton: true,
            confirmButtonColor: 'green',
            width: '400px',
            heightAuto: false,
            confirmButtonText: 'Continue',
            customClass: {
              icon: 'custom-swal-icon',
              title: 'custom-swal-title',
            },
          });
          if (result.isConfirmed) {
            navigate('/');
          }
          await setLicenseStatusValue('Activated');
        } else if (
          response.payload &&
          response.payload.data ===
            'This product key is already in use. Use a different key or Deactivate your old instance first.'
        ) {
          SweetSweetAlert({ sweetData: response.payload.data, icon: 'error', buttonColor: 'red' });
        } else if ((response.payload && response.payload.status === 404) || response.payload.status === 400) {
          SweetSweetAlert({ sweetData: response.payload.data, icon: 'error', buttonColor: 'red' });
        } else {
          SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'info', buttonColor: 'blue' });
        }
      } catch (error) {
        SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const result = await Swal.fire({
          icon: 'info',
          title: 'Are you sure to deactivate?',
          showConfirmButton: true,
          confirmButtonColor: 'var(--bg-button-default-color)',
          width: '400px',
          heightAuto: false,
          showCancelButton: true,
          cancelButtonColor: 'green',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          reverseButtons: true,
          customClass: {
            container: 'custom-swal-container',
            icon: 'custom-swal-icon',
            title: 'custom-swal-title',
          },
        });
        if (result.isConfirmed) {
          setLoading(true);
          const response = await dispatch(deActivateLicense(value));
          if (response.payload && response.payload.status === 200) {
            await SweetSweetAlert({ sweetData: response.payload.data, icon: 'success', buttonColor: 'green' });
            await setLicenseStatusValue('Deactivated');
            await setReCall(true);
          } else if (response.payload && response.payload.data === 'Already Deactivated') {
            SweetSweetAlert({ sweetData: response.payload.data, icon: 'info', buttonColor: 'blue' });
          } else if ((response.payload && response.payload.status === 404) || response.payload.status === 400) {
            SweetSweetAlert({ sweetData: response.payload.data, icon: 'error', buttonColor: 'red' });
          } else {
            SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'info', buttonColor: 'blue' });
          }
        } else {
          setActive(true);
        }
      } catch (error) {
        SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <AdminPageWrap className='af-superadmin-activation-wrap'>
      {loading && (
        <LoaderComponent>
          <Loader />
        </LoaderComponent>
      )}
      <AdminPageSizeWrap className='af-superadmin-activation-content'>
        {licenseDetails?.isActived && (
          <BackButton className='af-superadmin-activation-backbutton' to='/'>
            <LeftArrowIcon className='af-superadmin-activation-leftarrow' /> Back
          </BackButton>
        )}
        <AdminTitleWrap className='af-superadmin-activation-title-wrap'>
          <h2 className='af-superadmin-activation-title-head' style={{ marginBottom: '0px' }}>
            Activation Details
          </h2>
        </AdminTitleWrap>
        <AntDivider className='af-superadmin-activation-divider' />
        <ContentWrapper className='af-superadmin-activation-inside-content'>
          <StatusWrap className='af-superadmin-activation-status-wrap'>
            <StatusLabel className='af-superadmin-activation-status-label'>Status:</StatusLabel>
            <StatusValue className='af-superadmin-activation-status-value'>{licenseStatusValue}</StatusValue>
          </StatusWrap>
          <Form
            className='af-superadmin-activation-form-element'
            form={form}
            layout='vertical'
            onFinish={handleSubmit}
            initialValues={{ email: '', licenseKey: '' }}
          >
            <Form.Item
              className='af-superadmin-activation-form-item-productcode'
              name='productCode'
              label='Product Code'
              style={{ display: 'none' }}
            >
              <Input readOnly className='input_noChange' />
            </Form.Item>
            <FormContentWrap className='af-superadmin-activation-formcontent-wrap'>
              <EmailWrap className='af-superadmin-activation-emailwrap'>
                <FormItem
                  className='af-superadmin-activation-form-item-email'
                  name='email'
                  label='Email ID'
                  style={{ marginBottom: 0 }}
                >
                  <Input className='af-superadmin-activation-input-email' disabled={licenseDetails?.isActived} />
                </FormItem>
                <EmailFigCaptionWrap className='af-superadmin-activation-email-captionwrap'>
                  <span className='af-superadmin-activation-email-caption-require' style={{ color: 'red' }}>
                    *
                  </span>
                  <FigCaption className='af-superadmin-activation-email-caption'>
                    The email id you used to purchase this product.
                  </FigCaption>
                </EmailFigCaptionWrap>
              </EmailWrap>
              <LicenseKeyWrap className='af-superadmin-activation-key-wrap'>
                <FormItem className='af-superadmin-activation-form-item-key' name='licenseKey' label='Product Key'>
                  <LicenseInputWrap className='af-superadmin-activation-key-inputwrap'>
                    {licenseParts.map((part, index) => (
                      <React.Fragment key={index}>
                        <Input
                          className='af-superadmin-activation-input-key'
                          disabled={licenseDetails?.isActived}
                          ref={(el) => (inputRefs.current[index] = el)}
                          value={part}
                          onChange={(e) => handleInputChange(index, e.target.value)}
                          onPaste={(e) => handlePaste(e, index)}
                          onKeyDown={(e) => {
                            if (e.key === 'Backspace' && part === '' && index > 0) {
                              inputRefs.current[index - 1].focus();
                            }
                          }}
                          style={{
                            width: '78px',
                            marginRight: '5px',
                            height: '32px',
                          }}
                        />
                        {index < 3 && <span style={{ marginRight: '5px' }}>-</span>}
                      </React.Fragment>
                    ))}
                  </LicenseInputWrap>
                </FormItem>
              </LicenseKeyWrap>
            </FormContentWrap>
            <FormItem className='toggleWrap af-superadmin-activation-form-item-submit'>
              <CustomToggleButton active={active} setActive={setActive} isSubmitDisabled={isSubmitDisabled} />
            </FormItem>
          </Form>
        </ContentWrapper>
      </AdminPageSizeWrap>
    </AdminPageWrap>
  );
}
